import {
  Box,
  Button,
  Text as GammaText,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import { EmployeeBlocksEmployeeFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Country from 'modules/core/components/Inputs/Country'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import LabeledInput from 'modules/core/components/LabeledInput'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import {
  useAddListToContractCuesContext,
  useHasContractCue,
} from 'modules/contracts/hooks/useHasContractCue'
import ContractCuesAlert from 'modules/contracts/pages/ContractVersionsPage/ContractCuesAlert'
import type { GeneralForm } from 'modules/employees/components/blocks/General/GeneralBlock'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  form: GeneralForm
  readonly?: boolean
}

const Fields = ({ readonly, form, employee }: Props) => {
  const today = moment()
  const {
    addFirstName,
    addLastName,
    addPreferredPronouns,
    addBirthDate,
    chooseLegalGender,
    chooseNationality,
    addPhoneNumber,
    addCommunicationsEmail,
  } = trackingEvents.employee.personal

  const email = employee.email
  const genderOptions =
    employee.legalEntity?.legalGenders?.map((legalGender) => {
      return {
        label: legalGender.gender.translatedName,
        value: legalGender.gender.name,
      }
    }) ?? []

  const fieldLabels: Record<string, string> = {
    first_name: i18n.t('employee_form.first_name'),
    last_name: i18n.t('employee_form.last_name'),
    birthday_on: i18n.t('employee_form.birthday'),
    gender: i18n.t('employee_form.gender'),
    nationality: i18n.t('employee_form.nationality'),
    phone_number: i18n.t('employee_form.phone_number'),
    communications_email: i18n.t('employee_form.communications_email.label'),
  }

  const ifSharePreferredName = employee._policies?.sharePreferredName?.can || false
  const inFrance = employee.legalEntity?.country === 'fr'
  const maxDate = today.clone().subtract(14, 'years')

  const hasContractCue = useHasContractCue()
  const listOfCuedFields = Object.keys(form.fields).filter((field) => {
    return hasContractCue(field) && !form.get(field).originalValue
  })
  listOfCuedFields.concat(
    Object.keys(form.fields).filter((field) => {
      return hasContractCue(field) && !form.get(field).originalValue
    })
  )

  const translatedList = listOfCuedFields.map((field) => fieldLabels[field])
  useAddListToContractCuesContext('generalInfo', translatedList)

  const sameCommsEmail =
    form.get('communications_email').value === email &&
    form.dirtyFieldsKeys.includes('communications_email')

  return (
    <Box id="generalInfo">
      <FormRow>
        <Text
          mandatory
          field={form.get('first_name')}
          label={fieldLabels.first_name}
          readonly={readonly}
          onBlur={() => track(addFirstName)}
        />
      </FormRow>

      <FormRow>
        <Text
          mandatory
          field={form.get('last_name')}
          label={fieldLabels.last_name}
          readonly={readonly}
          onBlur={() => track(addLastName)}
        />
      </FormRow>

      {ifSharePreferredName && (
        <FormRow>
          <LabeledInput
            isLegend
            label={i18n.t('employee_form.preferred_name')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t('employee_form.preferred_name_information')}
                icon="question"
              />
            }
          >
            <Text field={form.get('preferred_name')} readonly={readonly} />
          </LabeledInput>
        </FormRow>
      )}

      {hasFeature('pronouns') && (
        <FormRow>
          <Text
            label={i18n.t('employee_form.pronouns')}
            field={form.get('pronouns')}
            readonly={readonly}
            placeholder={i18n.t('employee_form.pronouns_placeholder')}
            onBlur={() => track(addPreferredPronouns)}
          />
        </FormRow>
      )}

      {inFrance && (
        <FormRow>
          <Text
            label={i18n.t('employee_form.birth_name')}
            field={form.get('birth_name')}
            readonly={readonly}
          />
        </FormRow>
      )}

      <FormRow>
        <Box flexDirection="column" gap="s8">
          <DayPicker
            mandatory
            label={fieldLabels.birthday_on}
            field={form.get('birthday_on')}
            readonly={readonly}
            maxDate={maxDate}
            onChange={() => track(addBirthDate)}
          />
          <TogglerCheckbox
            labelWeight="normal"
            value={!form.get('share_birthday').value}
            onChange={(value) => form.setValues({ share_birthday: !value })}
            label={i18n.t('employee_form.hide_birthday')}
            information={i18n.t('employee_form.hide_birthday_tooltip')}
            disabled={readonly}
          />
        </Box>
      </FormRow>

      {inFrance && (
        <>
          <FormRow>
            <Country
              label={i18n.t('employee_form.country_of_birth')}
              field={form.get('country_of_birth')}
              readonly={readonly}
            />
          </FormRow>
          <FormRow>
            <Text
              label={i18n.t('employee_form.birthplace')}
              field={form.get('birthplace')}
              readonly={readonly}
            />
          </FormRow>
        </>
      )}

      <FormRow>
        <Select
          mandatory
          options={genderOptions}
          field={form.get('gender')}
          label={fieldLabels.gender}
          readonly={readonly}
          none={i18n.t('common.choose')}
          onBlur={() => track(chooseLegalGender)}
        />
      </FormRow>

      <FormRow>
        <Country
          mandatory
          label={fieldLabels.nationality}
          field={form.get('nationality')}
          readonly={readonly}
          none={i18n.t('common.none')}
          onBlur={() => track(chooseNationality)}
        />
      </FormRow>

      <FormRow>
        <Text
          label={fieldLabels.phone_number}
          field={form.get('phone_number')}
          readonly={readonly}
          onBlur={() => track(addPhoneNumber)}
        />
      </FormRow>

      <FormRow>
        <LabeledInput
          isLegend
          label={i18n.t('employee_form.communications_email.label')}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('employee_form.communications_email.information')}
              icon="question"
            />
          }
        >
          <Text
            field={form.get('communications_email')}
            readonly={readonly}
            onBlur={() => track(addCommunicationsEmail)}
          />
        </LabeledInput>
        {form.get('unconfirmed_communications_email').originalValue &&
          form.get('unconfirmed_communications_email').originalValue !== email && (
            <GammaText size="200">
              {i18n.t('employee_form.unconfirmed_description', {
                email: form.get('unconfirmed_communications_email').originalValue,
              })}
            </GammaText>
          )}
        {sameCommsEmail && (
          <GammaText size="200">{i18n.t('employee_form.same_communications_email')}</GammaText>
        )}
      </FormRow>

      {form.isDirty && (
        <FormRow footer>
          <Button submit>{i18n.t('common.submit')}</Button>
        </FormRow>
      )}

      {!readonly && <ContractCuesAlert fields={translatedList}></ContractCuesAlert>}
    </Box>
  )
}

export default observer(Fields)
